import React from 'react';
import {
    Box,
    VStack,
    Text,
    Input,
    Select,
    Button,
    FormLabel,
    Switch,
    Divider,
    IconButton,
    SimpleGrid,
    Flex,
    Icon
} from '@chakra-ui/react';
import {
    FaPlus,
    FaTrash,
    FaSave,
    FaClock,
} from 'react-icons/fa';
import { FiMessageSquare } from 'react-icons/fi';

const SmsInboundNodeProperties = ({ nodeConfig, setNodeConfig, updateNodeData }) => {
    return (
        <VStack spacing={4} align="stretch">
            <Box>
                <FormLabel fontSize="sm">Step Name</FormLabel>
                <Input
                    value={nodeConfig.label}
                    onChange={(e) => setNodeConfig({ ...nodeConfig, label: e.target.value })}
                    placeholder="e.g., Confirm Appointment"
                    size="sm"
                />
                <Text fontSize="xs" color="gray.500" mt={1}>
                    This node waits for and routes inbound SMS responses from customers
                </Text>
            </Box>

            <Divider />

            <Box>
                <Flex justify="space-between" align="center" mb={3}>
                    <FormLabel fontSize="sm" mb={0}>Configure SMS Response Options</FormLabel>
                    <Button
                        size="xs"
                        fontSize="xs"
                        colorScheme="green"
                        onClick={() => {
                            const newResponses = [...(nodeConfig.smsResponses || [])];
                            const newId = `sms_${Date.now()}`;
                            newResponses.push({
                                key: '',
                                label: '',
                                id: newId,
                                timeoutEnabled: false,
                                timeoutDuration: 30,
                                timeoutUnit: 'seconds',
                            });
                            setNodeConfig({ ...nodeConfig, smsResponses: newResponses });
                        }}
                    >
                        Add Response
                    </Button>
                </Flex>

                {(!nodeConfig.smsResponses || nodeConfig.smsResponses.length === 0) ? (
                    <Box
                        textAlign="center"
                        py={8}
                        border="2px dashed"
                        borderColor="green.300"
                        borderRadius="lg"
                        bg="green.50"
                    >
                        <Icon as={FiMessageSquare} w={8} h={8} color="green.400" mb={2} />
                        <Text fontSize="sm" color="gray.600" mb={3}>
                            No SMS response options configured
                        </Text>
                        <Button
                            size="sm"
                            leftIcon={<Icon as={FaPlus} />}
                            colorScheme="green"
                            variant="outline"
                            onClick={() => {
                                const newId = `sms_${Date.now()}`;
                                setNodeConfig({
                                    ...nodeConfig,
                                    smsResponses: [{
                                        key: '',
                                        label: '',
                                        id: newId,
                                        timeoutEnabled: false,
                                        timeoutDuration: 30,
                                        timeoutUnit: 'seconds',
                                    }],
                                });
                            }}
                        >
                            Add First Response
                        </Button>
                    </Box>
                ) : (
                    <VStack spacing={3} align="stretch">
                        {(nodeConfig.smsResponses || []).map((response, index) => (
                            <Box
                                key={response.id || index}
                                p={3}
                                border="1px solid"
                                borderColor="green.300"
                                borderRadius="lg"
                                bg="green.50"
                            >
                                <Flex justify="space-between" align="center" mb={2}>
                                    <Text fontSize="xs" fontWeight="semibold">
                                        Response Option {index + 1}
                                    </Text>
                                    <IconButton
                                        icon={<Icon as={FaTrash} />}
                                        size="xs"
                                        colorScheme="red"
                                        variant="ghost"
                                        onClick={() => {
                                            const newResponses = (nodeConfig.smsResponses || []).filter((_, i) => i !== index);
                                            setNodeConfig({ ...nodeConfig, smsResponses: newResponses });
                                        }}
                                        aria-label="Delete response"
                                    />
                                </Flex>

                                <VStack spacing={3} align="stretch">
                                    <SimpleGrid columns={2} spacing={2}>
                                        <Box>
                                            <FormLabel fontSize="xs">Response Key</FormLabel>
                                            <Input
                                                bg="white"
                                                value={response.key || ''}
                                                onChange={(e) => {
                                                    const newResponses = [...(nodeConfig.smsResponses || [])];
                                                    newResponses[index] = { ...response, key: e.target.value };
                                                    setNodeConfig({ ...nodeConfig, smsResponses: newResponses });
                                                }}
                                                placeholder="e.g., 1"
                                                size="sm"
                                            />
                                        </Box>
                                        <Box>
                                            <FormLabel fontSize="xs">Label</FormLabel>
                                            <Input
                                                bg="white"
                                                value={response.label || ''}
                                                onChange={(e) => {
                                                    const newResponses = [...(nodeConfig.smsResponses || [])];
                                                    newResponses[index] = { ...response, label: e.target.value };
                                                    setNodeConfig({ ...nodeConfig, smsResponses: newResponses });
                                                }}
                                                placeholder="e.g., Confirm"
                                                size="sm"
                                            />
                                        </Box>
                                    </SimpleGrid>

                                    <Text fontSize="xs" color="gray.600">
                                        Enter the text/number the customer should reply with
                                    </Text>

                                    {/* Per-response wait timeout */}
                                    {/* <Box pt={2} borderTop="1px solid" borderColor="green.200">
                                        <Flex justify="space-between" align="center" mb={2}>
                                            <Box>
                                                <Text fontSize="xs" fontWeight="medium">Wait for Response</Text>
                                                <Text fontSize="10px" color="gray.600" mt={0.5}>
                                                    Check if reply received within time limit
                                                </Text>
                                            </Box>
                                            <Switch
                                                isChecked={response.timeoutEnabled || false}
                                                colorScheme="green"
                                                onChange={(e) => {
                                                    const newResponses = [...(nodeConfig.smsResponses || [])];
                                                    newResponses[index] = {
                                                        ...response,
                                                        timeoutEnabled: e.target.checked,
                                                        timeoutDuration: response.timeoutDuration || 30,
                                                        timeoutUnit: response.timeoutUnit || 'seconds',
                                                    };
                                                    setNodeConfig({ ...nodeConfig, smsResponses: newResponses });
                                                }}
                                            />
                                        </Flex>

                                        {response.timeoutEnabled && (
                                            <Box p={2} bg="white" border="1px solid" borderColor="green.200" borderRadius="md">
                                                <SimpleGrid columns={2} spacing={2}>
                                                    <Box>
                                                        <FormLabel fontSize="10px">Duration</FormLabel>
                                                        <Input
                                                            bg="white"
                                                            type="number"
                                                            min={1}
                                                            value={response.timeoutDuration || 30}
                                                            onChange={(e) => {
                                                                const newResponses = [...(nodeConfig.smsResponses || [])];
                                                                newResponses[index] = {
                                                                    ...response,
                                                                    timeoutDuration: parseInt(e.target.value) || 1,
                                                                };
                                                                setNodeConfig({ ...nodeConfig, smsResponses: newResponses });
                                                            }}
                                                            size="xs"
                                                        />
                                                    </Box>
                                                    <Box>
                                                        <FormLabel fontSize="10px">Unit</FormLabel>
                                                        <Select
                                                            bg="white"
                                                            value={response.timeoutUnit || 'seconds'}
                                                            onChange={(e) => {
                                                                const newResponses = [...(nodeConfig.smsResponses || [])];
                                                                newResponses[index] = {
                                                                    ...response,
                                                                    timeoutUnit: e.target.value,
                                                                };
                                                                setNodeConfig({ ...nodeConfig, smsResponses: newResponses });
                                                            }}
                                                            size="xs"
                                                        >
                                                            <option value="seconds">Seconds</option>
                                                            <option value="minutes">Minutes</option>
                                                            <option value="hours">Hours</option>
                                                            <option value="days">Days</option>
                                                        </Select>
                                                    </Box>
                                                </SimpleGrid>
                                            </Box>
                                        )}
                                    </Box> */}
                                </VStack>
                            </Box>
                        ))}
                    </VStack>
                )}
            </Box>

            <Divider />

            {/* No Response / Fallback */}
            <Box>
                <Flex justify="space-between" align="center" mb={3}>
                    <Box>
                        <Text fontSize="sm" fontWeight="medium">No Response / Fallback</Text>
                        <Text fontSize="xs" color="gray.600" mt={0.5}>
                            Configure wait time if no SMS response is received
                        </Text>
                    </Box>
                    <Switch
                        isChecked={nodeConfig.noResponseWaitEnabled || false}
                        colorScheme="green"
                        onChange={(e) => setNodeConfig({
                            ...nodeConfig,
                            noResponseWaitEnabled: e.target.checked,
                        })}
                    />
                </Flex>

                {nodeConfig.noResponseWaitEnabled && (
                    <Box p={3} border="1px solid" borderColor="gray.200" borderRadius="lg" bg="gray.50">
                        <Flex align="center" gap={2} fontSize="xs" color="orange.700" mb={3}>
                            <Icon as={FaClock} />
                            <Text fontWeight="medium">Wait Time Configuration</Text>
                        </Flex>

                        <SimpleGrid columns={2} spacing={2} mb={3}>
                            <Box>
                                <FormLabel fontSize="xs">Duration</FormLabel>
                                <Input
                                    type="number"
                                    min={1}
                                    value={nodeConfig.noResponseWaitTime || 24}
                                    onChange={(e) => setNodeConfig({
                                        ...nodeConfig,
                                        noResponseWaitTime: parseInt(e.target.value) || 1,
                                    })}
                                    size="sm"
                                />
                            </Box>
                            <Box>
                                <FormLabel fontSize="xs">Unit</FormLabel>
                                <Select
                                    value={nodeConfig.noResponseWaitUnit || 'hours'}
                                    onChange={(e) => setNodeConfig({
                                        ...nodeConfig,
                                        noResponseWaitUnit: e.target.value,
                                    })}
                                    size="sm"
                                >
                                    <option value="minutes">Minutes</option>
                                    <option value="hours">Hours</option>
                                    <option value="days">Days</option>
                                </Select>
                            </Box>
                        </SimpleGrid>

                        <Box fontSize="xs" color="gray.700" bg="gray.100" px={2} py={1.5} borderRadius="md">
                            <Text fontWeight="semibold" display="inline">Note: </Text>
                            The "No Response" path will trigger if no SMS reply is received within{' '}
                            {nodeConfig.noResponseWaitTime || 24} {nodeConfig.noResponseWaitUnit || 'hours'} from when the SMS is sent.
                        </Box>
                    </Box>
                )}
            </Box>

            <Button
                colorScheme="green"
                onClick={updateNodeData}
                leftIcon={<Icon as={FaSave} />}
            >
                Update Node
            </Button>
        </VStack>
    );
};

export default SmsInboundNodeProperties;
